import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';

import { Avatar, Button, FormLabel, Grid, Stack, TextField } from '@mui/material';

import { object, string, ValidationError } from 'yup';

import Axios from '../../Assets/Axios/Axios';

import { COLORS } from '../../Constant/COLORS';

import ToastContext from '../../Context/ToastContext';
import { useAuthContext } from '../../Context/AuthContext';

import Layout from './Components/Layout';
import { SectionLoader } from '../../Components/Loaders';
import { ErrorState } from '../../Components/ErrorStates';

import { base64ToFile, fileToBase64String, getClearedErrorObj, isDataUrl } from '../../Helpers';

import { useIsMobile } from '../../Hooks/useIsMobile';

import { FindProfessional } from '../../Server/Query/index';
import { UpdateProfessional } from '../../Server/Mutation';

import { uploadFile, deleteFile, UPLOAD_FOLDERS } from '../../Services/S3';

const MyDetails = () => {
  const { user } = useAuthContext();
  const { showToastMessage } = useContext(ToastContext);

  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [professionalInformation, setProfessionalInformation] = useState({
    firstName: '',
    lastName: '',
    address: '',
    province: '',
    postCode: '',
    phoneNumber: '',
    about: '',
    avatarURL: null,
  });

  const [errors, setErrors] = useState({
    firstName: null,
    lastName: null,
    address: null,
    province: null,
    postCode: null,
    about: null,
  });

  const currentAvatar = useRef(null);
  const avatarImageInput = useRef(null);
  const isMobile = useIsMobile();

  const fetchProfessionalDetails = useCallback(() => {
    setIsLoading(true);
    setLoadError(null);
    Axios(FindProfessional({ professionalUniqueID: user.userID }))
      .then(({ data, errors }) => {
        if (errors) {
          setLoadError("We couldn't load your professional details. Please try again.");
          return undefined;
        }

        if (!data?.Professional) {
          setLoadError("We couldn't find your profile. Please try again.");
          return undefined;
        }

        const professional = data.Professional;
        currentAvatar.current = professional.avatar;
        setProfessionalInformation(parseProfessional(professional));
      })
      .catch(() =>
        setLoadError("We couldn't reach the server. Please check your connection and try again."),
      )
      .finally(() => setIsLoading(false));
  }, [user.userID]);

  useEffect(() => {
    fetchProfessionalDetails();
  }, [fetchProfessionalDetails]);

  async function handleChangeImage(event) {
    const image = event.target.files?.item(0) ?? null;
    const base64Image = image && (await fileToBase64String(image));
    setProfessionalInformation({ ...professionalInformation, [event.target.name]: base64Image });
    event.target.value = '';
  }

  function handleChange({ target: { name, value } }) {
    setProfessionalInformation(currentData => ({ ...currentData, [name]: value }));
  }

  function handleClickCancel() {
    // fetchProfessionalDetails();
    setErrors(currentErrors => getClearedErrorObj(currentErrors));
  }

  /**
   *  @param {string|null} newImage
   *  @param {string|null} currentImage
   */
  async function uploadImageHandler(newImage, currentImage) {
    const isNewImageBase64Encoded = newImage !== null && isDataUrl(newImage);

    if (currentImage !== null && (newImage === null || isDataUrl(newImage)))
      await deleteFile(currentImage);

    if (isNewImageBase64Encoded) {
      const file = base64ToFile(newImage);
      return uploadFile(file, UPLOAD_FOLDERS.PROFESSIONAL_AVATAR);
    }

    return newImage;
  }

  async function handleClickSave() {
    setErrors(currentErrors => getClearedErrorObj(currentErrors));
    setIsSaveDisabled(true);
    try {
      const validatedData = await schema.validate(professionalInformation, {
        abortEarly: false,
      });
      // Uploads the new avatar to S3 (and removes the previous S3 object),
      // returning the S3 URL that gets persisted to the DB.
      const avatar = await uploadImageHandler(
        professionalInformation.avatarURL,
        currentAvatar.current,
      );

      const response = await Axios(
        UpdateProfessional({
          ...validatedData,
          professionalId: user.userID,
          postalCode: validatedData.postCode,
          avatar,
        }),
      );

      if (response.errors) {
        showToastMessage('Something went wrong while trying to update your details');
        return undefined;
      }

      fetchProfessionalDetails();
      showToastMessage('Updated successfully', 'success');
    } catch (error) {
      if (error instanceof ValidationError) {
        const validationErrors = error.inner.reduce(
          (acc, err) => ({ ...acc, [err.path]: err.message }),
          {},
        );
        setErrors(currentErrors => ({ ...currentErrors, ...validationErrors }));
      }
    } finally {
      setIsSaveDisabled(false);
    }
  }

  function handleClickDeletePic() {
    setProfessionalInformation(currentData => ({ ...currentData, avatarURL: null }));
  }

  if (isLoading) {
    return (
      <Layout
        message='Update your photo and personal details'
        isAvatarButtonsAvailable={false}
      >
        <SectionLoader height={360} />
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout
        message='Update your photo and personal details'
        isAvatarButtonsAvailable={false}
      >
        <ErrorState
          title="Couldn't load your details"
          message={loadError}
          onRetry={fetchProfessionalDetails}
        />
      </Layout>
    );
  }

  return (
    <Layout
      message='Update your photo and personal details'
      handleClickCancel={handleClickCancel}
      handleClickSave={handleClickSave}
      isSaveDisabled={isSaveDisabled}
    >
      <Grid
        container
        columns={7.5}
        maxWidth={{ md: 7 / 10 }}
        rowSpacing={{ xs: 1, md: 3 }}
        columnSpacing={2}
        alignItems='center'
        sx={{
          '.MuiFormLabel-root': {
            mt: { xs: 1.5, md: 'unset' },
          },
        }}
      >
        <Grid item xs={7.5} md={3} component={FormLabel} htmlFor='avatar'>
          Your Photo
        </Grid>

        <Grid item xs={7.5} md={4.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems='center' gap={2}>
            <Avatar
              src={professionalInformation.avatarURL ?? undefined}
              sx={{
                width: { xs: '50%', sm: '100px', md: '70px' },
                height: { xs: '50%', sm: '100px', md: '70px' },
                aspectRatio: '1',
              }}
            />
            <Stack direction='row' alignItems='center' gap={2} width='100%'>
              <Button
                fullWidth={isMobile}
                variant='text'
                sx={{ color: COLORS.blue, fontWeight: 600 }}
                onClick={handleClickDeletePic}
              >
                Delete
              </Button>
              <Button
                fullWidth={isMobile}
                variant='text'
                sx={{ color: COLORS.blue, fontWeight: 600 }}
                onClick={() => avatarImageInput.current?.click()}
              >
                Update
              </Button>
            </Stack>
            <input
              id='avatar'
              name='avatarURL'
              type='file'
              style={{ display: 'none' }}
              ref={avatarImageInput}
              accept='.png,.jpeg,.jpg'
              onChange={handleChangeImage}
            />
          </Stack>
        </Grid>

        <Grid item xs={7.5} sm={3} component={FormLabel}>
          First Name
        </Grid>
        <Grid item xs={7.5} sm={4}>
          <TextField
            fullWidth
            name='firstName'
            onChange={handleChange}
            helperText={errors.firstName}
            error={Boolean(errors.firstName)}
            value={professionalInformation.firstName}
            inputProps={{ spellCheck: false, style: { textTransform: 'capitalize' } }}
          />
        </Grid>

        <Grid item xs={7.5} sm={3} component={FormLabel}>
          Last Name
        </Grid>
        <Grid item xs={7.5} sm={4}>
          <TextField
            fullWidth
            name='lastName'
            onChange={handleChange}
            helperText={errors.lastName}
            error={Boolean(errors.lastName)}
            value={professionalInformation.lastName}
            inputProps={{ spellCheck: false, style: { textTransform: 'capitalize' } }}
          />
        </Grid>

        <Grid item xs={7.5} sm={3} component={FormLabel}>
          Address
        </Grid>
        <Grid item xs={7.5} sm={4}>
          <TextField
            fullWidth
            name='address'
            onChange={handleChange}
            helperText={errors.address}
            error={Boolean(errors.address)}
            inputProps={{ spellCheck: false }}
            value={professionalInformation.address}
          />
        </Grid>

        <Grid item xs={7.5} sm={3} component={FormLabel}>
          Province
        </Grid>
        <Grid item xs={7.5} sm={4}>
          <TextField
            fullWidth
            inputProps={{ spellCheck: false }}
            value={professionalInformation.province}
            name='province'
            onChange={handleChange}
            helperText={errors.province}
            error={Boolean(errors.province)}
          />
        </Grid>

        <Grid item xs={7.5} sm={3} component={FormLabel}>
          Post Code
        </Grid>
        <Grid item xs={7.5} sm={4}>
          <TextField
            fullWidth
            inputProps={{ spellCheck: false }}
            value={professionalInformation.postCode}
            name='postCode'
            onChange={handleChange}
            helperText={errors.postCode}
            error={Boolean(errors.postCode)}
          />
        </Grid>

        <Grid item xs={7.5} sm={3} component={FormLabel}>
          Phone Number
        </Grid>
        <Grid item xs={7.5} sm={4}>
          <TextField
            fullWidth
            inputProps={{ spellCheck: false }}
            InputProps={{ readOnly: true }}
            value={[professionalInformation.phoneNumber]}
          />
        </Grid>

        <Grid item xs={7.5} sm={3} component={FormLabel} alignSelf='flex-start'>
          About
        </Grid>
        <Grid item xs={7.5} sm={4}>
          <TextField
            rows={3}
            multiline
            fullWidth
            inputProps={{ spellCheck: false }}
            value={professionalInformation.about}
            name='about'
            onChange={handleChange}
            error={Boolean(errors.about)}
            helperText={errors.about ?? '*Make sure about is at least 150 characters'}
          />
        </Grid>
      </Grid>
    </Layout>
  );
};

function parseProfessional(professional) {
  return {
    firstName: professional.first_name,
    lastName: professional.last_name,
    address: professional.address_line_1,
    province: professional.province,
    postCode: professional.postal_code,
    phoneNumber: professional.mobile_number,
    avatarURL: professional.avatar,
    about: professional.about,
  };
}

const schema = object({
  firstName: string().label('First Name').trim().required(),
  lastName: string().label('last Name').trim().required(),
  address: string().label('Address').trim().required(),
  province: string().label('Province').trim().required(),
  postCode: string().label('Post Code').trim().required(),
  about: string().trim().min(150, 'Should be at least 150 characters.').required().label('About'),
});

export default MyDetails;
